import {TaskType} from '@components/calendar/calendar-store'
import {endOfDay, format, isAfter, isSameDay} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React, {useMemo} from 'react'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {
  getTaskHeight,
  minTaskHeight,
  proportionPixelsDivideOnHour,
  roundedTo15Min,
  roundNumberTo,
  validDateFormat
} from '../../utils'
import {useDraggableStore} from '../draggable/draggable-store'
import {BottomDeadline} from '../task/task-content'
import {TaskText} from '../task/task-text'
import {PlaceholderTw} from '../week'
import {ShowDraggedTime} from './hour'

interface Props {
  start: Date
  end: Date
  currentDate: Date
  currentHour: number
}

export const HourPlaceholder = observer(
  ({start, end, currentDate, currentHour}: Props) => {
    const [draggableState] = useDraggableStore()

    const memo = useMemo(
      () => (
        <PlaceholderContent
          start={start}
          end={end}
          currentDate={currentDate}
          currentHour={currentHour}
        />
      ),
      [
        draggableState.placeholderDate?.getMinutes() % 15 === 0,
        draggableState.placeholderDate?.getMinutes() % 10 === 0,
        roundNumberTo(draggableState.xTraveled, 10) %
          roundNumberTo(draggableState.parentWidth / 2, 10) ===
          0,
        draggableState.enabled
      ]
    )

    const fromDayMemo = useMemo(
      () => (
        <PlaceholderContent
          start={start}
          end={end}
          currentDate={currentDate}
          currentHour={currentHour}
        />
      ),
      [draggableState.placeholderDate, draggableState.enabled]
    )

    return draggableState.draggedItem.taskType === 'from-day-to-hour'
      ? fromDayMemo
      : memo
  }
)

const PlaceholderContent = observer(
  // eslint-disable-next-line max-lines-per-function
  ({start, end, currentDate, currentHour}: Props) => {
    const [draggableState] = useDraggableStore()

    const types: TaskType[] = [
      'all-day-multiple',
      'all-day-single',
      'all-day-with-time',
      'from-hour-to-day'
    ]

    const dragCondition = types.some(type =>
      draggableState.draggedItem.taskType?.includes(type)
    )

    if (draggableState.draggedItem.changeAction !== 'drag' || dragCondition) {
      return null
    }

    const startDate = draggableState.startDate
      ? draggableState.startDate
      : draggableState.deadline
    const deadline = draggableState.deadline

    if (
      validDateFormat(startDate, 'yyyy-MM-dd HH') ===
      validDateFormat(start, 'yyyy-MM-dd HH')
    ) {
      if (!draggableState.startDate) {
        return (
          <PlaceholderTw
            style={{
              top: roundedTo15Min(draggableState.deadline).getMinutes(),
              height: draggableState.draggedItem.height,
              backgroundColor: draggableState.draggedItem.backgroundColor
            }}
          >
            <ShowDraggedTime />
          </PlaceholderTw>
        )
      }
      const height = getTaskHeight(startDate, deadline)
      const minutes = startDate.getMinutes()
      const top = minutes * proportionPixelsDivideOnHour
      const hideDeadline = !isSameDay(deadline, end)
      const isAllDay =
        !isSameDay(deadline, currentDate) && !isSameDay(startDate, currentDate)
      return (
        <PlaceholderTw
          style={{
            top,
            height,
            backgroundColor: draggableState.draggedItem.backgroundColor
          }}
        >
          {height > minTaskHeight ? (
            <TaskText
              allDay={isAllDay}
              height={height}
              taskId={draggableState.draggedItem.id}
              hideDeadline={hideDeadline}
              realStartDate={Time.toTimestamp(startDate)}
              realDeadline={Time.toTimestamp(deadline)}
              gtdStage={draggableState.draggedItem.gtdStage}
              roundedDeadline={deadline}
              columnColor={draggableState.draggedItem.backgroundColor}
              title={draggableState.draggedItem.title}
              isColorDark={draggableState.draggedItem.isColorDark}
              assigned={draggableState.draggedItem.assigned}
              isSubtask={draggableState.draggedItem.isSubtask}
            />
          ) : (
            <ShowDraggedTime />
          )}
        </PlaceholderTw>
      )
    }

    if (
      isSameDay(deadline, start) &&
      !isSameDay(startDate, end) &&
      startDate < end &&
      deadline > start
    ) {
      const isAllDay =
        !isSameDay(deadline, currentDate) && !isSameDay(startDate, currentDate)
      if (
        !isAllDay &&
        currentHour === 0 &&
        isAfter(currentDate, endOfDay(startDate))
      ) {
        const height = getTaskHeight(startDate, deadline, {
          startFromZero: true
        })
        const top = 0
        const hideDeadline = !isSameDay(deadline, end)
        return (
          <PlaceholderTw
            style={{
              top,
              height,
              backgroundColor: draggableState.draggedItem.backgroundColor
            }}
          >
            <TaskText
              allDay={isAllDay}
              height={height}
              taskId={draggableState.draggedItem.id}
              hideDeadline={!hideDeadline}
              realStartDate={Time.toTimestamp(startDate)}
              realDeadline={Time.toTimestamp(deadline)}
              gtdStage={draggableState.draggedItem.gtdStage}
              roundedDeadline={deadline}
              columnColor={draggableState.draggedItem.backgroundColor}
              title={draggableState.draggedItem.title}
              isColorDark={draggableState.draggedItem.isColorDark}
              assigned={draggableState.draggedItem.assigned}
              isSubtask={draggableState.draggedItem.isSubtask}
              timeWithDate
            />
            {height > 45 && (
              <BottomDeadline>
                {Lang.msg.calendar.deadline}:{' '}
                {format(draggableState.deadline, 'HH:mm')}
              </BottomDeadline>
            )}
          </PlaceholderTw>
        )
      }
    }

    if (
      !isSameDay(deadline, start) &&
      !isSameDay(startDate, end) &&
      startDate < end &&
      deadline > start
    ) {
      const isAllDay =
        !isSameDay(deadline, currentDate) && !isSameDay(startDate, currentDate)
      if (isAllDay && currentHour === 0) {
        const height = getTaskHeight(startDate, deadline, {
          allDay: true,
          startFromZero: true
        })
        const top = 0
        const hideDeadline = !isSameDay(deadline, end)
        return (
          <PlaceholderTw
            style={{
              top,
              height,
              backgroundColor: draggableState.draggedItem.backgroundColor
            }}
          >
            <TaskText
              allDay={isAllDay}
              height={height}
              taskId={draggableState.draggedItem.id}
              hideDeadline={!hideDeadline}
              realStartDate={Time.toTimestamp(startDate)}
              realDeadline={Time.toTimestamp(deadline)}
              gtdStage={draggableState.draggedItem.gtdStage}
              roundedDeadline={deadline}
              columnColor={draggableState.draggedItem.backgroundColor}
              title={draggableState.draggedItem.title}
              isColorDark={draggableState.draggedItem.isColorDark}
              assigned={draggableState.draggedItem.assigned}
              isSubtask={draggableState.draggedItem.isSubtask}
            />
          </PlaceholderTw>
        )
      }
    }
    return null
  }
)
